import _ from 'lodash';
import { type UnknownAction } from '@reduxjs/toolkit'

import {
  GENERATE_TRANSCRIPTS_FOR_CALL_RECORDING_REQUEST,
  GENERATING_TRANSCRIPTS_FOR_CALL_RECORDING_SUCCEEDED,
  GENERATING_TRANSCRIPTS_FOR_CALL_RECORDING_FAILED,
  DELETE_CALL_RECORDING_REQUEST,
  DELETING_CALL_RECORDING_SUCCEEDED,
  DELETING_CALL_RECORDING_FAILED,
  FETCH_CALL_RECORDINGS_REQUEST,
  FETCHING_CALL_RECORDINGS_SUCCEEDED,
  FETCHING_CALL_RECORDINGS_FAILED,
  FETCH_CALL_RECORDING_REQUEST,
  FETCHING_CALL_RECORDING_FAILED,
  FETCHING_CALL_RECORDING_SUCCEEDED,
  WS_CALL_RECORDING_PROFILE_GENERATION_COMPLETED,
  SYNCING_CALL_RECORDING_PROFILE_PROCESSING_STATE_SUCCEEDED,
  SYNC_CALL_RECORDING_PROFILE_PROCESSING_STATE_REQUEST
} from "../../actions/actionTypes";

import {
  FetchCallRecordingsRequest,
  FetchCallRecordingsResponse,
  FetchCallRecordingsErrorResponse,
  DeleteCallRecordingRequest,
  DeleteCallRecordingResponse,
  DeleteCallRecordingErrorResponse,
  FetchCallRecordingRequest,
  CallRecording,
  FetchCallRecordingResponse,
  FetchCallRecordingErrorResponse,
  CallRecordingProfileProcessing,
  GenerateTranscriptForCallRecordingRequest,
  GenerateTranscriptForCallRecordingResponse,
  GenerateTranscriptForCallRecordingErrorResponse,
  CallRecordingProcessingStatus
} from 'src/types/coaching/callRecording';
import { call } from 'redux-saga/effects';

interface CallRecordingsState {
  loading: boolean;
  loaded: boolean;
  items: CallRecording[];
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
  error: any | null;
  call: CallRecordingState;
  deletion: {
    [key: string]: CallRecordingDeletionState;
  };
  upload: {
    [key: string]: uploadState;
  };
}

interface CallRecordingState {
  loading: boolean;
  loaded: boolean;
  syncing?: boolean;
  synced?: boolean;
  item: CallRecording | null;
  error: any;
}

interface uploadState {
  uploading: boolean,
  uploaded: boolean,
}

interface CallRecordingDeletionState {
  id: string,
  deleting: boolean,
  deleted: boolean,
  error: any
}

const initialState: CallRecordingsState = {
  loading: false,
  loaded: false,
  items: [],
  pagination: {
    currentPage: 0,
    totalPages: 0,
    recordsPerPage: 0,
    totalRecordsCount: 0,
    hasMoreItems: false
  },
  error: null,
  call: {
    loading: false,
    loaded: false,
    item: null,
    error: null,
  },
  deletion: {},
  upload: {},
}

export function callRecordingReducer(state: CallRecordingsState = initialState, action: UnknownAction): CallRecordingsState {
  switch (action.type) {
    case GENERATE_TRANSCRIPTS_FOR_CALL_RECORDING_REQUEST: {
      const { id } = action.payload as GenerateTranscriptForCallRecordingRequest;
      
      return {
        ...state,
        upload: {
          ...state.upload,
          [id]: {
            uploading: true,
            uploaded: false
          }
        }
      };
    }

    case GENERATING_TRANSCRIPTS_FOR_CALL_RECORDING_SUCCEEDED: {
      const { id, response } = action.payload as GenerateTranscriptForCallRecordingResponse;

      const existingItems: any[] = state.items || [];
      const updatedRecords = [response, ...existingItems];

      return {
        ...state,    
        upload: {},   
        items: updatedRecords
      };
    }

    case FETCH_CALL_RECORDINGS_REQUEST: {
      const { page } = action.payload as FetchCallRecordingsRequest;

      let existingItems: any[] = state.items || [];
      if (page === 0) {
        existingItems = [];
      }

      return {
        ...state,
        items: existingItems,
        loading: true,
        loaded: false,
        error: null,
      };
    }

    case FETCHING_CALL_RECORDINGS_SUCCEEDED: {
      const { response } = action.payload as FetchCallRecordingsResponse;

      if (!response) {
        return state;
      }

      const existingItems: any[] = state.items || [];
      let records = response.records;

      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      return {
        ...state,
        loading: false,
        loaded: true,
        items: records,
        pagination: {
          currentPage: response.currentPage,
          totalPages: response.totalPages,
          recordsPerPage: response.recordsPerPage,
          totalRecordsCount: response.totalRecordsCount,
          hasMoreItems: response.currentPage < response.totalPages - 1,
        },
        error: null,
      };
    }

    case FETCHING_CALL_RECORDINGS_FAILED: {
      const { error } = action.payload as FetchCallRecordingsErrorResponse;

      return {
        ...state,
        loading: false,
        loaded: false,
        error: error,
        items: [],
      };
    }

    case DELETE_CALL_RECORDING_REQUEST: {
      const {id} = action.payload as DeleteCallRecordingRequest;
      let deletionState = state.deletion[id];

      deletionState = {
        ...deletionState,
        id,
        deleting: true,
        deleted: false,
        error: null,
      };

      return {
        ...state,
        deletion: {
          ...state.deletion,
          [id]: deletionState,
        },
      };
    }

    case DELETING_CALL_RECORDING_SUCCEEDED: {
      const { id } = action.payload as DeleteCallRecordingResponse;
      const deletionState = state.deletion[id];
      if (!deletionState) {
        return state;
      }

      const items = state.items?.filter((item) => {
        return item.id !== id;
      });


      return {
        ...state,
        items: items,
        deletion: {
          ...state.deletion,
          [id]: {
            ...deletionState,
            deleting: false,
            deleted: true,
            error: null,
          },
        },
      };
    }

    case DELETING_CALL_RECORDING_FAILED: {
      const { id, error } = action.payload as DeleteCallRecordingErrorResponse;
      const deletionState = state.deletion[id];
      if (!deletionState) {
        return state;
      }

      return {
        ...state,
        deletion: {
          ...state.deletion,
          [id]: {
            ...deletionState,
            deleting: false,
            deleted: false,
            error: error,
          },
        },
      };
    }

    case FETCH_CALL_RECORDING_REQUEST: {
      const { id } = action.payload as FetchCallRecordingRequest;

      return {
        ...state,
        call: {
          ...state.call,
          loading: true,
          loaded: false,
          error: null,
        },
      };
    }

    case FETCHING_CALL_RECORDING_SUCCEEDED: {
      const { id, response } = action.payload as FetchCallRecordingResponse;

      return {
        ...state,
        call: {
          ...state.call,
          loading: false,
          loaded: true,
          item: response,
          error: null,
        },
      };
    }

    case FETCHING_CALL_RECORDING_FAILED: {
      const { id, error } = action.payload as FetchCallRecordingErrorResponse;

      return {
        ...state,
        call: {
          ...state.call,
          loading: false,
          loaded: false,
          error: error,
        },
      };
    }

    case WS_CALL_RECORDING_PROFILE_GENERATION_COMPLETED: {
      const { id } = action.payload as any;
    
      const updatedItems = state.items.map((item) => {
        if (item.id !== id) return item;
        
        return {
          ...item,
          profileExtractionProcessing: {
            id: id,
            status: 'completed' as CallRecordingProcessingStatus
          },
        };
      });
    
      return {
        ...state,
        items: updatedItems,
      };
    }

    case SYNC_CALL_RECORDING_PROFILE_PROCESSING_STATE_REQUEST: {
      const { id } = action.payload as FetchCallRecordingRequest;

      return {
        ...state,
        call: {
          ...state.call,
          syncing: true,
          synced: false,
        },
      };
    }

    case SYNCING_CALL_RECORDING_PROFILE_PROCESSING_STATE_SUCCEEDED: {
      const {id, response } = action.payload as FetchCallRecordingResponse;

      if(!response.id || !id) {
        return state;
      }

      const updatedItems = state.items.map((item) => {
        if (item.id !== id) return item;
        
        return {
          ...item,
          ...response          
        };
      });

      return {
        ...state,
        call: {
          ...state.call,
          syncing: false,
          synced: true,
        },
        items: updatedItems,
      };
    }

    default: {
      return state;
    }
  }
}